import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useRememberedScroll } from '../../hooks/useRemembered';
import { recall, remember } from '../../services/viewMemory';
import {
    FileText,
    Plus,
    AlertTriangle,
    CheckCircle2,
    RefreshCw,
    ShieldAlert,
    Layers,
    Landmark,
    FileSpreadsheet,
} from 'lucide-react';
import {
    Customer,
    ExternalAdvance,
    Invoice,
    InvoiceConfig,
    UserProfile,
} from '../../types';
import { Button } from '../Button';
import { KontextHilfe } from '../lernzentrum/KontextHilfe';
import type { TutorialKapitelId } from '../../tutorial/types';
import { createPickerStock, PickerStock, PickerStockLoad } from './pickerStockLogic';
import {
    fetchInvoice,
    fetchInvoices,
    buildProjectServiceLines,
    createAdvanceInvoice,
    createFinalInvoice,
    createInvoiceDraft,
    createSplitAdvanceInvoices,
    createSplitInvoiceDrafts,
    fetchAllExternalAdvances,
    markInvoicesExported,
} from '../../services/invoiceService';
import { fetchProjectSplit } from '../../services/projectSplitService';
import {
    createInvoiceFromProject,
    describeCreateFailure,
    prepareInvoiceCreate,
} from './invoiceCreateFlow';
import { fetchCustomers, setProjectCustomer } from '../../services/crmService';
import { billingDocTypeForProject } from '../../services/projectBilling';
import { mergeCustomerIntoList } from '../../services/customerQuickEdit';
import { rechtFehlerOder, rechtFehlerText } from '../../services/rechte/rechtFehler';
import { useRechte } from '../../hooks/useRechte';
import { buchhaltungNurLesen, rechnungAktionen } from './rechnungRechte';
import { ListNavKey, nextListIndex } from '../../services/listKeyboardNav';
import { CustomerPickerDialog } from '../crm/CustomerPickerDialog';
import {
    DOC_TYPE_CHOICES,
    EMPTY_ADVANCE_DRAFT,
    readDocPreference,
    writeDocPreference,
    type CreatableDocumentType,
    type Section,
} from './invoicesViewShared';
import { SectionTabs } from './invoicesViewParts';
import {
    AdvanceAmountStep,
    BillingModeStep,
    DeductAdvancesStep,
    PickerStepBadge,
    PriorMonthsDialog,
} from './pickerSteps';
import { missingCustomerMessage, projectContextNames } from '../project-hub/customerLinkLogic';
import {
    invalidateProjectsLoadCache,
    loadProjectsFromCloud,
} from '../../services/cloudService';
import type { ListProject } from '../../services/listProject';
import {
    invoiceListProjectRefs,
    invoiceProjectLabel,
    openMonthsBeforePeriod,
} from '../../services/invoiceLogic';
import { isDeductibleAdvance } from '../../services/advanceInvoiceLogic';
import { formatCents } from '../../services/money';
import { localToday } from '../../services/localToday';
import { buildActorName } from '../../services/actorName';
import {
    awaitsCoverageCheck,
    establishedBillingModeByProject,
    OPEN_BILLING_STANCE,
    type BillingStance,
} from '../../services/billingMode';
import {
    clearPickerAnswer,
    effectivePickerAnswers,
    EMPTY_PICKER_ANSWERS,
    nextPickerStep,
    pickerStepPosition,
    previousPickerStep,
    readAdvanceAmount,
    type AdvanceAmountChoice,
    type PickerAnswers,
    type PickerStepFacts,
    type PickerStepId,
} from '../../services/invoicePickerFlow';
import {
    tallyProjectDocuments,
} from '../../services/projectDocumentTally';
import { FlatMonthSuggestion } from '../../services/flatRateBooking';
import { bookFlatMonthOnCloud } from '../../services/flatRateBookingService';
import { FlatMonthBookingDialog } from '../FlatMonthBookingDialog';
import { refreshProjectList } from './backlogDatingLogic';
import {
    billableRows,
    billingStancesForRows,
    buildPickerProjectRows,
    filterPickerProjects,
    relevantNetCentsFor,
    totalRelevantNetCents,
    uncoveredPriorBillingForRows,
} from './pickerProjectRows';
import { useBacklogDating } from './useBacklogDating';
import { usePaymentsStream } from './usePaymentsStream';
import {
    computeInvoiceStats,
    customersByPaymentDelay,
    issuedByPeriod,
    IssuedGranularity,
    monthlyInvoiceVolume,
    receivablesAging,
    topCustomersByRevenue,
} from '../../services/invoiceStats';
import {
    clampInvoiceListPage,
    invoiceCustomerOptions,
    invoiceListPageCount,
    invoiceListPageItems,
    invoiceListTotals,
    visibleInvoiceList,
} from './invoiceListLogic';
import { useInvoiceListFilters } from './useInvoiceListFilters';
import { InvoiceListFilterBar, InvoiceListTable } from './InvoiceListTable';
import { ProjectPickerDialog } from './ProjectPickerDialog';
import { InvoiceEditor } from './InvoiceEditor';
import { ProjectCheckSweep } from './ProjectCheckSweep';
import { BillingRunWizard } from './BillingRunWizard';
import { InvoiceKpiBar } from './InvoiceKpiBar';
import { BankImportDialog } from './BankImportDialog';
import { DatevExportDialog, DatevExportMarkResult } from './DatevExportDialog';
import { InvoiceCharts, IssuedBreakdownPanel } from './InvoiceCharts';
import { MarkPaidDialog } from './MarkPaidDialog';
import { AdminAccountingView } from '../admin/AdminAccountingView';

/**
 * Rechnungsuebersicht.
 *
 * Aufbau wie QuotesView: eine Liste, die bei Auswahl gegen den Editor getauscht
 * wird — keine eigene Route. Der Nutzer bleibt damit in derselben Ansicht,
 * und der Zurueck-Weg ist eindeutig.
 */

interface Props {
    invoiceConfig: InvoiceConfig;
    // senderLine wird von App.tsx geliefert und an den Editor durchgereicht —
    // sie gehoert in den Typ, sonst faellt sie beim ersten Zugriff auf.
    company: { name?: string; address?: string; logo?: string; footerText?: string; senderLine?: string };
    quoteConfig?: unknown;
    userProfile: UserProfile | null;
    onOpenProject?: (project: ListProject) => void;
    /**
     * Sprung aus einem Projekt in die Buchhaltung.
     *
     * Gesetzt heisst: dieses Projekt sofort aufschlagen, nicht die Belegliste.
     */
    initialAccountingProjectId?: string;
    /**
     * Kontext-"?" in der Kopfzeile (Lernzentrum). Fehlt der Rueckruf, fehlt
     * das Fragezeichen — die Ansicht haengt nicht daran.
     */
    onOeffneLernzentrum?: (kapitelId?: TutorialKapitelId) => void;
    /**
     * Sprung aus der Kundenakte: diesen Beleg nach dem Laden oeffnen. Laeuft
     * ueber denselben Weg wie die Rueckkehr aus dem Projekt (pendingOpenId).
     */
    initialInvoiceId?: string;
    /** Schliessen DIESES Belegs fuehrt dorthin zurueck, woher gesprungen wurde. */
    onCloseInitialInvoice?: () => void;
}

export const InvoicesView: React.FC<Props> = ({
    invoiceConfig,
    company,
    quoteConfig,
    userProfile,
    onOpenProject,
    initialAccountingProjectId,
    onOeffneLernzentrum,
    initialInvoiceId,
    onCloseInitialInvoice,
}) => {
    // Kommt der Aufruf aus einem Projekt, soll die Buchhaltung offen sein —
    // die Belegliste waere dort ein Umweg.
    // Der Sprung "Projekt -> Buchhaltung" erzwingt den Abrechnungsreiter; sonst
    // der zuletzt gewaehlte, damit der Rueckweg aus einem Projekt wieder dort landet.
    const [section, setSection] = useState<Section>(
        () => initialAccountingProjectId ? 'accounting' : (recall<Section>('invoices.section') ?? 'documents'),
    );
    useEffect(() => { remember('invoices.section', section); }, [section]);
    // Rollensystem: was man in dieser Ansicht TUN darf (rechnungRechte).
    // Admins haben jedes Recht — fuer sie bleibt jeder Knopf frei wie bisher.
    const rechte = useRechte(userProfile?.role);
    const darf = useMemo(() => rechnungAktionen(rechte), [rechte]);
    // Ohne Recht auf die Buchhaltung zeigt die Ansicht die Belege, auch beim
    // Sprung aus einem Projekt. Der gemerkte Reiter bleibt stehen und greift,
    // sobald die Rechte geladen sind.
    const sichtbarerAbschnitt: Section =
        section === 'accounting' && !darf.buchhaltung.erlaubt ? 'documents' : section;
    const accountingScrollRef = useRememberedScroll('invoices.accounting.scroll');
    const documentsScrollRef = useRememberedScroll('invoices.documents.scroll');
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [projects, setProjects] = useState<ListProject[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Erfolgsmeldungen aus dem Editor: dort gesetzt wuerden sie nie sichtbar,
    // weil Storno, Korrektur und Gutschrift den Editor sofort unmounten.
    const [notice, setNotice] = useState<string | null>(null);
    // Filter, Suche, Sortierung und Seite der Belegliste — gemerkt unter
    // denselben Schluesseln wie bisher (useInvoiceListFilters).
    const {
        filter,
        setFilter,
        search,
        setSearch,
        customerFilter,
        setCustomerFilter,
        typeFilter,
        setTypeFilter,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        sort,
        setSort,
        pageSize,
        setPageSize,
        page,
        setPage,
        filtersActive,
        resetFilters,
    } = useInvoiceListFilters();
    const [editing, setEditing] = useState<Invoice | null>(null);
    /**
     * Der Beleg, dessen Positionen gerade nachgeladen werden.
     *
     * Die Liste laedt sie nicht mit; zwischen Klick und Editor liegt deshalb
     * eine Abfrage. Ohne sichtbare Rueckmeldung sieht ein langsames Netz aus,
     * als haette der Klick nicht gewirkt — und der zweite Klick startet die
     * Abfrage ein zweites Mal.
     */
    const [opening, setOpening] = useState<string | null>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    /**
     * Belege MIT Positionen — ausschliesslich fuer den Anlagedialog.
     *
     * Die Belegliste kommt ohne Positionen aus, die Vorschau der offenen Mengen
     * nicht: computeOpenPositions und openByMonth lesen aus den Zeilen der
     * vorhandenen Belege, welche Protokolleintraege bereits abgerechnet sind.
     * Fehlten die Zeilen, gaelte alles als offen — der Dialog boete Mengen an,
     * die laengst in Rechnung stehen, und zwar ohne jede Fehlermeldung. Deshalb
     * wird der volle Bestand erst beim Oeffnen des Dialogs geholt, und bis
     * dahin zeigt der Dialog gar keine Projekte statt falscher Zahlen.
     */
    const [pickerInvoices, setPickerInvoices] = useState<Invoice[] | null>(null);
    /** 'failed' zeigt im Dialog den Wiederholen-Knopf statt der Ladezeile. */
    const [pickerLoad, setPickerLoad] = useState<PickerStockLoad>('idle');
    /**
     * Die Ladesteuerung des Anlagedialogs.
     *
     * Sie liegt in pickerStockLogic und nicht in einem Effekt hier: ein Effekt,
     * der seinen eigenen Ladezustand beobachtet, verwirft sein eigenes Ergebnis
     * (Begruendung dort).
     *
     * Gehalten in einem Ref und NICHT in einem useMemo: React darf einen
     * gemerkten Wert jederzeit wegwerfen und neu berechnen. Dann entstuende
     * eine zweite Steuerung, waehrend die Antwort der ersten noch unterwegs
     * ist — sie schriebe ueber die stabilen Setter in eine Steuerung, die von
     * ihrer Anforderung nichts weiss, und der neue Regler haelte einen Bestand
     * fuer aktuell, den er nie geholt hat. Ein Ref wird garantiert nicht
     * verworfen.
     */
    const pickerStockRef = useRef<PickerStock | null>(null);
    if (!pickerStockRef.current) {
        pickerStockRef.current = createPickerStock<Invoice>({
            fetchAll: () => fetchInvoices(),
            setStock: setPickerInvoices,
            setLoad: setPickerLoad,
            reportError: setError,
        });
    }
    const pickerStock = pickerStockRef.current;
    /**
     * Externe Alt-Abschlaege je Projekt — `null` heisst „noch nicht ermittelt".
     *
     * Gebraucht fuer den Zwang zur fortschreibenden Anlage: er greift nur bei
     * Altfakturierung OHNE Belegdeckung, und ein erfasster Alt-Abschlag ist
     * genau diese Deckung. Ohne diesen Bestand liesse sich der zugesagte Ausweg
     * — Altrechnung erfassen, Zwang faellt weg — gar nicht abbilden.
     */
    const [externalAdvances, setExternalAdvances] = useState<Map<
        string,
        ExternalAdvance[]
    > | null>(null);
    /**
     * Der Ladezustand DIESER Abfrage — und warum er nicht in `null` passt.
     *
     * `externalAdvances === null` hiess bisher zweierlei: „laeuft noch" und
     * „fehlgeschlagen". Die beiden fuehren aber zu entgegengesetzten
     * Entscheidungen. Der Fehlschlag darf keine Abrechnungsart erzwingen — die
     * Frage wird gestellt, mit Hinweis, und der Beleg laeuft notfalls in die
     * Sperre beim Ausstellen. Der laufende Ladevorgang dagegen ist gar keine
     * Aussage: die Projektliste haengt an `pickerInvoices` und steht schon,
     * waehrend diese Abfrage noch unterwegs ist. In genau diesem Fenster sah
     * ein Bestandsprojekt mit ungedeckter Altfakturierung wie ein frisches aus,
     * und „kumulativ" war waehlbar — die Bauart von Vorfall 6172, abgesichert
     * allein durch einen Hinweistext.
     */
    const [coverageLoad, setCoverageLoad] = useState<'loading' | 'ready' | 'failed'>(
        'loading',
    );
    /**
     * Der Abruf dazu — bewusst NEBEN dem Belegbestand und ohne ihn aufzuhalten.
     *
     * Die Projektliste des Dialogs haengt an `pickerInvoices`, nicht hieran:
     * fehlen die Alt-Abschlaege noch, erscheinen die Projekte trotzdem, nur
     * ohne Zwang und ohne Ansage. Das ist die richtige Richtung — ein
     * Ladefehler darf keine Abrechnungsart erzwingen. Der falsch angelegte
     * Beleg laeuft dann in die Sperre beim Ausstellen
     * (prior_billing_not_deducted), die dieselbe Frage stellt.
     *
     * Bei JEDEM Oeffnen neu und nicht einmalig gehalten: der Betreiber erfasst
     * den Alt-Abschlag in der Buchhaltung (ExternalAdvancesPanel) und kommt
     * hierher zurueck. Wuerde der Bestand von vorhin weiterverwendet, stuende
     * der Zwang noch, obwohl er sich laengst aufgeloest hat.
     *
     * Kein Effekt, der seinen eigenen Ladezustand beobachtet (die Falle, die
     * pickerStockLogic beschreibt): abhaengig ist er allein vom geoeffneten
     * Dialog, und die Antwort eines abgeraeumten Durchlaufs wird verworfen.
     */
    useEffect(() => {
        if (!pickerOpen) return;
        let dropped = false;
        // Beim Oeffnen zurueck auf „laeuft": der Bestand von vorhin darf nicht
        // als geprueftes Ergebnis dieses Durchlaufs durchgehen. Der Betreiber
        // erfasst den Alt-Abschlag zwischendurch in der Buchhaltung, und genau
        // dann waere die alte Antwort die falsche.
        setExternalAdvances(null);
        setCoverageLoad('loading');
        void (async () => {
            try {
                const rows = await fetchAllExternalAdvances();
                if (!dropped) {
                    setExternalAdvances(rows);
                    setCoverageLoad('ready');
                }
            } catch (e) {
                // Nicht ins Fehlerbanner: der Dialog ist voll benutzbar, es
                // entfaellt nur eine Vorwegnahme. Protokolliert werden muss es
                // trotzdem — faellt die Abfrage dauerhaft aus, sieht man es
                // sonst nirgends.
                console.error(
                    'Externe Alt-Abschläge konnten nicht geladen werden; die fortschreibende Anlage wird nicht erzwungen.',
                    e,
                );
                if (!dropped) {
                    setExternalAdvances(null);
                    setCoverageLoad('failed');
                }
            }
        })();
        return () => {
            dropped = true;
        };
    }, [pickerOpen]);
    // Belegart des naechsten Entwurfs. Sie steht im Anlagedialog und nicht im
    // Editor: aus ihr folgt, WIE die Positionen entstehen — LV-Positionen,
    // eine Abschlagspauschale oder LV-Positionen samt Abschlagsabzug. Ein
    // spaeterer Wechsel waere ein anderer Beleg, kein geaendertes Feld.
    // Die Wahl aus der letzten Sitzung, nur als Vorbelegung gelesen.
    const [savedPreference] = useState(readDocPreference);
    const [docType, setDocType] = useState<CreatableDocumentType>(
        () => savedPreference?.docType ?? 'INVOICE',
    );
    /**
     * Die Eingaben des Schritts „Höhe des Abschlags", als ENTWURF — und zwar
     * AN DAS PROJEKT GEBUNDEN.
     *
     * Bewusst in der Ansicht und nicht in der Schrittkomponente: die wird beim
     * Zurueck- und Wiedervorgehen aus- und eingehaengt, und die getippten
     * Zahlen waeren jedes Mal weg. Die geprueften Werte landen in den Antworten
     * der Kette, dieser Entwurf ist nur, was in den Feldern steht.
     *
     * Die Projektnummer daneben ist der Kern: ohne sie ueberlebte der Entwurf
     * den Projektwechsel. Wer fuer ein Projekt „Fester Betrag / 25.000,00"
     * eingibt und danach ein Projekt mit 8.000 € Auftragswert anklickt, faende
     * die 25.000 vorbelegt — ein Abschlag ueber das Dreifache des Auftrags, aus
     * einem Wert, den niemand fuer dieses Projekt eingegeben hat. Das ist
     * derselbe projektuebergreifend gemerkte Dialogzustand, dessen Abschaffung
     * der Zweck dieses Umbaus ist (Vorfall 6172).
     */
    const [advanceDraft, setAdvanceDraft] = useState<{
        projectId: string | null;
        choice: AdvanceAmountChoice;
    }>({ projectId: null, choice: EMPTY_ADVANCE_DRAFT });
    // Abrechnungszeitraum der Teilrechnung. Er gehoert in den Anlagedialog und
    // nicht erst in den Editor: die Positionen entstehen beim Anlegen, und wer
    // den Zeitraum nachtraeglich setzt, hat bis zum Neuaufbau eine Rechnung
    // ueber den gesamten offenen Bestand mit der Kopfangabe eines Monats.
    const [periodFrom, setPeriodFrom] = useState('');
    const [periodTo, setPeriodTo] = useState('');
    const [includeUndated, setIncludeUndated] = useState(false);
    /** Suche in der Projektliste des Anlagedialogs. */
    const [projectSearch, setProjectSearch] = useState('');
    /**
     * Suchfeld und Scrollflaeche der Projektliste — fuer die Tastatur.
     *
     * Das Suchfeld bekommt beim Oeffnen den Fokus (der erste Griff war bisher
     * immer die Maus), und von dort fuehren die Pfeiltasten in die Liste. Die
     * Scrollflaeche wird gebraucht, um die Zeilenknoepfe zu finden: ein Ref je
     * Zeile waere bei zweihundert Projekten zweihundert Refs, die bei jedem
     * Tastendruck der Suche neu entstehen.
     */
    const pickerSearchRef = useRef<HTMLInputElement | null>(null);
    const pickerListRef = useRef<HTMLDivElement | null>(null);
    const [creating, setCreating] = useState<string | null>(null);
    const [sweepOpen, setSweepOpen] = useState(false);
    const [runOpen, setRunOpen] = useState(false);
    // Zahlungen der Kennzahlen: Bestand, Ladezustand, Meldung und die
    // Ladesteuerung samt Abbruch beim Verlassen (usePaymentsStream; dort auch,
    // warum die Steuerung nicht in einem useMemo steht und warum der Startwert
    // 'loading' ist).
    const { payments, setPayments, paymentsLoad, paymentsError, paymentsStream, paymentsPending } =
        usePaymentsStream();
    // Die Auswertung ist eingeklappt: sie beantwortet eine andere Frage als die
    // Liste, und wer eine Rechnung sucht, will sie nicht erst wegscrollen.
    const [chartsOpen, setChartsOpen] = useState(false);
    // "Gestellt" aufgeschluesselt je Monat, Woche oder Tag. Eigener Schalter an
    // der Kachel statt Teil der Auswertung: die Frage "was war es im Juli?"
    // stellt sich an der Kachel, nicht erst hinter dem Auswertungs-Link.
    const [issuedOpen, setIssuedOpen] = useState(false);
    const [issuedGranularity, setIssuedGranularity] = useState<IssuedGranularity>('month');
    const [bankOpen, setBankOpen] = useState(false);
    const [datevOpen, setDatevOpen] = useState(false);
    /**
     * Der Beleg, auf den aus der LISTE heraus eine Zahlung gebucht wird.
     *
     * Gehalten wird der Beleg selbst und nicht seine id: die Vorbelegung des
     * Dialogs (offener Betrag, Steuersaetze) steht in der Belegzeile, die
     * bereits geladen ist — ein Nachladen waere genau der Umweg ueber den
     * Editor, den dieser Weg erspart. Die Positionen braucht er nicht.
     */
    const [markPaid, setMarkPaid] = useState<Invoice | null>(null);


    const today = localToday();

    /**
     * Faellt dieser Monat in den gewaehlten Zeitraum?
     *
     * Auf Monatsgenauigkeit — ein Zeitraum, der mitten im Monat beginnt, faerbt
     * den ganzen Monat ein. Fuer eine Uebersicht ist das richtig: sie soll
     * zeigen, WO noch etwas liegt, nicht den Rechnungsbetrag vorwegnehmen.
     */
    const monthInPeriod = (month: string | null): boolean => {
        if (!periodFrom && !periodTo) return false;
        if (!month) return includeUndated;
        if (periodFrom && month < periodFrom.slice(0, 7)) return false;
        if (periodTo && month > periodTo.slice(0, 7)) return false;
        return true;
    };

    // Sofort merken, nicht erst beim Anlegen: wer den Dialog schliesst, ohne
    // eine Rechnung zu erzeugen, hat die Wahl trotzdem getroffen.
    useEffect(() => {
        writeDocPreference({ docType });
    }, [docType]);

    /**
     * Nach welchem Modell die bereits AUSGESTELLTEN Belege eines Projekts
     * rechnen.
     *
     * Die Regel steht in billingMode.ts und nicht hier: derselbe Schluss wird
     * im Rechnungslauf gezogen (billingRunService), und zwei Fassungen davon
     * liefen unweigerlich auseinander. Entscheidend ist, dass Belege OHNE
     * Aussage — Korrektur, Gutschrift, Storno einer Abschlagsrechnung —
     * uebersprungen und nicht als "fortschreibend" gelesen werden.
     */
    const priorBillingMode = useMemo(
        () => establishedBillingModeByProject(invoices),
        [invoices],
    );

    /**
     * Wie viele Belege je Projekt schon da sind — ausgestellt und als offener
     * Entwurf, bewusst getrennt (Begruendung in projectDocumentTally.ts).
     *
     * Dieselbe Quelle wie priorBillingMode: die ohnehin geladene Belegliste.
     * Kein zusaetzlicher Abruf, und anders als der Bestand des Anlagedialogs
     * steht sie auch, waehrend dieser noch geholt wird.
     */
    const documentTallyByProject = useMemo(() => tallyProjectDocuments(invoices), [invoices]);

    /**
     * Wie viele abzugsfaehige Abschlaege ein Projekt hat.
     *
     * Entscheidet, ob die Rueckfrage „Abschläge abziehen?" ueberhaupt gestellt
     * wird. Dieselbe Pruefung, mit der createFinalInvoice sie spaeter findet
     * (isDeductibleAdvance) — sonst fragte der Dialog nach etwas, das der Beleg
     * nicht abzieht, oder schwiege, wo es etwas abzuziehen gibt.
     *
     * Aus der ohnehin geladenen Belegliste, ohne zusaetzlichen Abruf: gezaehlt
     * wird am Kopf des Belegs (Belegart und Status), Positionen braucht es
     * dafuer nicht.
     */
    const deductibleAdvancesByProject = useMemo(() => {
        const out = new Map<string, number>();
        for (const inv of invoices) {
            if (!inv.primaryProjectId || !isDeductibleAdvance(inv)) continue;
            out.set(inv.primaryProjectId, (out.get(inv.primaryProjectId) ?? 0) + 1);
        }
        return out;
    }, [invoices]);

    /**
     * Merkt sich den offenen Beleg fuer den Rueckweg aus dem Projekt.
     *
     * Der Sprung ins Projekt unmountet die ganze Ansicht; der Zurueck-Weg der
     * Anwendung fuehrt auf die Rechnungsliste, nicht auf den Beleg. Ohne diesen
     * Merker landet der Nutzer nach "Projekt oeffnen" wieder bei der Liste und
     * muss seine Rechnung suchen.
     *
     * Bewusst sessionStorage und ein eigener Schluessel: der gemeinsame
     * Navigationszustand kennt nur View, Projekt und Modul, und ihn zu
     * erweitern hiesse, den Wiederherstellungspfad aller Ansichten anzufassen.
     */
    const OPEN_INVOICE_KEY = 'invoices.openInvoiceId';
    const rememberOpen = (id: string | null) => {
        try {
            if (id) window.sessionStorage.setItem(OPEN_INVOICE_KEY, id);
            else window.sessionStorage.removeItem(OPEN_INVOICE_KEY);
        } catch {
            // Privater Modus oder volles Kontingent — der Merker ist Komfort,
            // nie eine Bedingung.
        }
    };

    // Beim ersten Rendern lesen, nicht erst im Effekt: der Effekt, der den
    // Merker pflegt, wuerde ihn sonst mit dem Anfangszustand (nichts offen)
    // ueberschreiben, bevor das Laden ihn auswerten kann.
    // Ein Sprung aus der Kundenakte geht dem gemerkten Beleg vor.
    const pendingOpenId = useRef<string | null>(
        initialInvoiceId ??
        (() => {
            try {
                return window.sessionStorage.getItem(OPEN_INVOICE_KEY);
            } catch {
                return null;
            }
        })(),
    );
    // Solange der angesprungene Beleg offen ist, fuehrt Zurueck in die Akte.
    const rueckwegInvoiceIdRef = useRef<string | null>(initialInvoiceId ?? null);

    /**
     * Einen Beleg zum Bearbeiten oeffnen.
     *
     * Er wird vollstaendig nachgeladen, weil die Liste ohne Positionen kommt.
     * Ein aus der Liste durchgereichter Beleg traege `lines: []` — der Editor
     * zeigte einen leeren Beleg, und das erste Speichern machte diese Leere
     * dauerhaft. Deshalb wird hier nichts durchgereicht, sondern geholt, und
     * bei einem Fehlschlag bleibt die Liste stehen und meldet es.
     */
    const openInvoice = useCallback(async (id: string) => {
        setOpening(id);
        setError(null);
        try {
            const full = await fetchInvoice(id);
            if (!full) {
                setError(
                    'Dieser Beleg ist nicht mehr vorhanden. Bitte die Liste aktualisieren.',
                );
                return;
            }
            // Den geladenen Stand auch in die Liste zurueckschreiben: er ist
            // juenger als der beim Laden geholte, und der Editor gibt ihn ueber
            // onChange ohnehin wieder an dieselbe Stelle zurueck.
            setInvoices((prev) => prev.map((i) => (i.id === full.id ? full : i)));
            setEditing(full);
        } catch (e: any) {
            setError(
                e?.message ||
                    'Der Beleg konnte nicht geladen werden. Er wurde deshalb nicht geöffnet.',
            );
        } finally {
            setOpening(null);
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        // Der Anlagedialog haelt seinen eigenen, vollstaendigen Bestand. Nach
        // einem Neuladen ist der ueberholt und wird beim naechsten Oeffnen neu
        // geholt — sonst rechnete die Vorschau mit dem Stand von vorhin. Eine
        // noch laufende Abfrage wird dabei verworfen, damit sie den gerade
        // aufgegebenen Bestand nicht doch noch einspielt.
        pickerStock.invalidate();
        // Der Projekt-Loader teilt gleichzeitige Aufrufer auf EINEN laufenden
        // Request (cloudService.loadProjectsFromCloud, gegen die Dreifachlast
        // beim Login). Nach einem Schreibvorgang ist das die Falle: die
        // Ruecknahme einer Buchung schreibt in zwei Schritten (RPC entfernt die
        // Zeile, dann der Save mit Zaehler und Spur), der erste Schritt loest
        // ueber Realtime schon einen Load aus, und dieses Neuladen haengte
        // sich an den — der Stand von VOR dem zweiten Schritt. Der Eintrag
        // stand danach weiter in der Liste, bis jemand die Seite neu lud
        // (6110, 02.09.2026). Ein ausdrueckliches Neuladen holt deshalb
        // immer frisch; die anderen Schreibwege hier tun dasselbe.
        invalidateProjectsLoadCache();
        try {
            const [inv, cust, proj] = await Promise.all([
                // Ohne Positionen: die Tabelle zeigt Nummer, Kunde, Projekt,
                // Datum, Betraege und Status — keine davon steht in `lines`,
                // und die Spalte ist die groesste der Tabelle.
                fetchInvoices({ withoutLines: true }),
                fetchCustomers(),
                loadProjectsFromCloud(),
            ]);
            setInvoices(inv);
            setCustomers(cust);
            setProjects(proj);

            // Zahlungen erst NACH den Rechnungen, weil sie deren IDs brauchen —
            // aber ausdruecklich OHNE await: sie werden fuer die Liste nicht
            // gebraucht (die Spalte "Offen" rechnet aus den Summen der
            // Belegzeile), und abgewartet verzoegerten sie jede Belegzeile um
            // eine zweite Abfrage. Bis die Antwort da ist, zeigen die beiden
            // zahlungsabhaengigen Auswertungen "wird geladen" statt einer Zahl.
            //
            // Die Steuerung sperrt dabei die spaete Antwort eines aelteren
            // Laufs aus: load() hat viele Ausloeser, und ohne diese Sperre
            // schriebe der ueberholte Lauf seine Zahlungen ueber die neuen.
            paymentsStream.load(inv.map((i) => i.id));

            // Rueckkehr aus dem Projekt: den Beleg wieder oeffnen, von dem aus
            // gesprungen wurde. Findet er sich nicht mehr (geloescht, fremder
            // Mandant), bleibt es bei der Liste — kein Fehler, nur kein Sprung.
            // Noch innerhalb des Ladens, damit die Ladeanzeige stehen bleibt,
            // bis der Beleg wirklich offen ist.
            const openId = pendingOpenId.current;
            pendingOpenId.current = null;
            if (openId && inv.some((i) => i.id === openId)) {
                await openInvoice(openId);
            }
        } catch (e: any) {
            setError(e?.message || 'Die Rechnungen konnten nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    }, [openInvoice, pickerStock, paymentsStream]);

    useEffect(() => {
        void load();
    }, [load]);

    /**
     * Die Positionen erst holen, wenn der Anlagedialog sie braucht. Damit zahlt
     * der taegliche Blick in die Belegliste nicht fuer eine Vorschau, die nur
     * beim Anlegen gebraucht wird.
     */
    const openPicker = useCallback(() => {
        setPickerOpen(true);
        pickerStock.open();
    }, [pickerStock]);

    const closePicker = useCallback(() => {
        setPickerOpen(false);
        pickerStock.close();
        // Die Rueckfrage-Kette gehoert zu diesem Dialog und wird ausserhalb von
        // `pickerOpen` gezeichnet. Ohne dieses Abraeumen schwebte der offene
        // Schritt nach einem Klick auf das Schliessen-X ueber der Belegliste,
        // und der Escape-Griff kam an ihn nicht mehr heran.
        setFlow(null);
        setAssignCustomerError(null);
        // Und die Deckungspruefung faengt beim naechsten Oeffnen von vorn an.
        // Der Belegbestand ueberlebt das Schliessen bewusst; stuende hier noch
        // 'ready' vom letzten Mal, zeigte die Liste beim Wiederoeffnen einen
        // gemalten Frame lang Projekte als 'open', deren Deckung noch gar nicht
        // geprueft ist — mit klickbarer Zeile.
        setCoverageLoad('loading');
    }, [pickerStock]);

    // Verlaesst der Nutzer die Ansicht, waehrend der volle Bestand noch geholt
    // wird, laeuft die Abfrage sonst zu Ende und spielt ihr Ergebnis in Setter
    // einer nicht mehr vorhandenen Ansicht. Das Schliessen verwirft sie.
    useEffect(() => () => pickerStock.close(), [pickerStock]);


    // Ein Effekt statt eines Aufrufs an jeder setEditing-Stelle: sechs Stellen
    // aendern den offenen Beleg, und eine vergessene liesse den Merker auf
    // einer Rechnung stehen, die gar nicht mehr offen ist.
    // Der Unterdruecker hier ist entfallen: er verdeckte nicht `rememberOpen`,
    // sondern die Warnung ueber `window` — und die war berechtigt, siehe
    // `statsWindow` unten. Ohne die Verdeckung meldet die Regel nichts mehr.
    useEffect(() => {
        rememberOpen(editing?.id ?? null);
    }, [editing?.id]);

    /**
     * Auswertungsfenster: die letzten zwoelf Monate.
     *
     * Betrifft nur die FLUSSGROESSEN (Umsatz, vereinnahmt, DSO). Die
     * Stichtagsgroessen — offene Forderungen, ueberfaellig, Entwuerfe —
     * beschreiben den Bestand von heute und ignorieren das Fenster.
     *
     * Der Name ist NICHT beliebig: als `window` verdeckte diese Konstante das
     * gleichnamige Browser-Objekt in der gesamten Komponente. `window.
     * sessionStorage` weiter oben lief damit gegen dieses Objekt und warf —
     * gefangen vom try/catch, also lautlos: der gemerkte offene Beleg
     * funktionierte nie. Jedes weitere `window.…` in dieser Komponente haette
     * dasselbe Schicksal.
     */
    const statsWindow = useMemo(() => {
        const to = today;
        const d = new Date(`${today}T00:00:00Z`);
        d.setUTCMonth(d.getUTCMonth() - 11);
        d.setUTCDate(1);
        return { from: d.toISOString().slice(0, 10), to };
    }, [today]);

    const periodLabel = 'letzte 12 Monate';

    const stats = useMemo(
        () => computeInvoiceStats(invoices, payments, { today, ...statsWindow }),
        [invoices, payments, today, statsWindow],
    );

    const charts = useMemo(
        () => ({
            monthly: monthlyInvoiceVolume(invoices, { today, ...statsWindow }),
            aging: receivablesAging(invoices, { today }),
            topCustomers: topCustomersByRevenue(invoices, { today, ...statsWindow }),
            paymentBehaviour: customersByPaymentDelay(invoices, payments, { today, ...statsWindow }),
        }),
        [invoices, payments, today, statsWindow],
    );

    // Ohne Fenster: die Aufschluesselung deckelt sich selbst (24 Monate, 26
    // Wochen, 60 Tage), und "was habe ich im Juli geschrieben" haengt nicht
    // am Zeitraumfilter. Nur gerechnet, solange sie offen ist.
    const issuedBreakdown = useMemo(
        () => (issuedOpen ? issuedByPeriod(invoices, { today, granularity: issuedGranularity }) : []),
        [invoices, today, issuedOpen, issuedGranularity],
    );

    /**
     * Projektbezug eines Belegs als eine Zeile: "Nummer · Name".
     *
     * Die geladenen Projekte fuellen nur die Luecke bei Belegen aus der Zeit
     * vor details.projects — sonst gilt der Vermerk am Beleg, damit Liste,
     * Maske und PDF dasselbe zeigen.
     *
     * invoiceListProjectRefs und nicht invoiceProjectRefs: nur HIER darf der
     * eingefrorene Vermerk auch dann zaehlen, wenn der Beleg ohne Positionen
     * geladen wurde (Sammelrechnung ohne primaryProjectId). Der Andruck des
     * Belegs und die Typziffer der Belegnummer bleiben davon unberuehrt.
     *
     * Einmal je Beleg berechnet und in einer Karte gehalten: die Liste braucht
     * die Angabe zweimal — beim Sortieren und beim Zeichnen der Zeile —, und
     * jede Berechnung durchsuchte bisher die geladenen Projekte von vorn. Bei
     * tausend Belegen und hundert Projekten waren das zweihunderttausend
     * Vergleiche, und zwar bei jedem Tastendruck in der Suche.
     */
    const projectLabels = useMemo(() => {
        const byId = new Map<string, ListProject>(projects.map((p) => [p.id, p]));
        const lookup = (id: string) => {
            const p = byId.get(id);
            return p ? { id, number: p.project_number, name: p.name } : undefined;
        };
        const labels = new Map<string, string>();
        for (const inv of invoices) {
            labels.set(inv.id, invoiceProjectLabel(invoiceListProjectRefs(inv, lookup)));
        }
        return labels;
    }, [invoices, projects]);

    /**
     * Nachschlagen statt rechnen. Leer heisst hier dasselbe wie zuvor: kein
     * Projektbezug — ein Beleg, der nicht in der Karte steht, kommt auch nicht
     * in der Liste vor.
     */
    const projectLabelOf = useCallback(
        (inv: Invoice): string => projectLabels.get(inv.id) ?? '',
        [projectLabels],
    );

    /**
     * Der Name des Rechnungsempfaengers — GELADENER Kunde zuerst.
     *
     * `inv.customerName` ist der beim Laden mitgelesene Join-Name und friert
     * den Stand dieses Abrufs ein. Wird der Kunde aus dem Beleg heraus
     * umbenannt (Schnelldialog im Editor, `onCustomerChange` zieht die
     * Kundenliste nach), aktualisieren sich Belegkopf und Vorschau, die Liste
     * dahinter aber nicht — und das Speichern sah aus, als waere es
     * gescheitert. Der Join-Name bleibt der Rueckfall fuer Kunden, die gar
     * nicht geladen sind (archiviert, geloescht).
     *
     * Dieselbe Ableitung fuer Anzeige, Suche und Sortierung: drei Stellen mit
     * drei Faellen sind drei Gelegenheiten, eine davon zu vergessen.
     */
    const customerNameOf = useCallback(
        (inv: Invoice): string =>
            customers.find((c) => c.id === inv.customerId)?.name || inv.customerName || '',
        [customers],
    );

    const visible = useMemo(() => {
        // Filtern und Sortieren steht in invoiceListLogic (reine Funktionen,
        // dort getestet); hier nur das Nachschlagen und die Abhaengigkeiten.
        return visibleInvoiceList(
            invoices,
            { filter, search, customerFilter, typeFilter, dateFrom, dateTo },
            sort,
            { today, customerNameOf, projectLabelOf },
        );
    }, [
        invoices,
        filter,
        search,
        today,
        customerFilter,
        typeFilter,
        dateFrom,
        dateTo,
        sort,
        projectLabelOf,
        // Ohne diese Abhaengigkeit blieben Suche und Sortierung auf dem
        // Namensstand des letzten Ladens stehen, waehrend die Anzeige daneben
        // schon den neuen zeigt.
        customerNameOf,
    ]);

    const pageCount = invoiceListPageCount(visible.length, pageSize);
    // Geklemmt statt korrigiert: ein Filter kann die Trefferzahl jederzeit unter
    // die aktuelle Seite druecken. Ueber den State zu gehen zeigte dabei fuer
    // einen Frame eine leere Tabelle.
    const currentPage = clampInvoiceListPage(page, pageCount);
    const pageItems = useMemo(
        () => invoiceListPageItems(visible, currentPage, pageSize),
        [visible, currentPage, pageSize],
    );

    /** Was die gefilterte Liste zusammen ausmacht — die Zahl unter dem Strich. */
    const visibleTotals = useMemo(() => invoiceListTotals(visible), [visible]);

    /** Kunden, die in der Belegliste ueberhaupt vorkommen. */
    const customerOptions = useMemo(() => invoiceCustomerOptions(invoices), [invoices]);

    /**
     * Projekte als Grundlage des Anlagedialogs — bewusst UNGEFILTERT.
     *
     * Welche Projekte in Frage kommen, haengt von der Belegart ab. Eine
     * Abschlagsrechnung wird VOR der Leistung gestellt; ein Projekt ohne offene
     * Menge ist dort der Normalfall und kein Ausschlussgrund.
     *
     * Gerechnet wird in pickerProjectRows (buildPickerProjectRows, reine
     * Funktion, dort getestet) — auch der Riegel "keine Zeile, solange der
     * Dialog zu ist oder die Belegpositionen fehlen". Hier nur die
     * Abhaengigkeiten.
     */
    const projectRows = useMemo(
        () =>
            buildPickerProjectRows({
                projects,
                pickerOpen,
                pickerInvoices,
                periodFrom,
                periodTo,
                includeUndated,
                today,
            }),
        // pickerInvoices gehoert dazu: aus ihren Positionen kommt die Zuordnung,
        // und nach dem Ausstellen einer Rechnung muss die Uebersicht sofort
        // stimmen — load() verwirft den Bestand deshalb bei jedem Neuladen.
        [projects, periodFrom, periodTo, includeUndated, pickerInvoices, pickerOpen, today],
    );

    /**
     * MONATSPAUSCHALE: der Buchungsdialog aus dem Anlagedialog heraus.
     * Bewusst ein SCHNAPPSCHUSS der Vorschlaege (nicht die lebende Zeile):
     * nach der Buchung wird der Projektbestand neu geladen, und die Zeile
     * kann dabei aus der Liste fallen — der Dialog soll dann trotzdem seine
     * Erfolgsansage zeigen. Ein zweiter Buchungsversuch aus dem alten Stand
     * prallt am Schluessel-Riegel des Schreibpfads ab (flatRateBookingService
     * laedt frisch und plant erneut).
     */
    const [flatBooking, setFlatBooking] = useState<{
        projectId: string;
        projectName: string;
        suggestions: FlatMonthSuggestion[];
    } | null>(null);

    /**
     * Ungedeckte Altfakturierung je Projekt — der zweite Grund fuer die
     * fortschreibende Anlage.
     *
     * Ueber 150 Projekte tragen im Leistungsverzeichnis bereits abgerechnete
     * Menge, zu der es in OS:IA keinen einzigen Beleg gibt: ihre
     * Abrechnungshistorie liegt im Altsystem. Sie haben deshalb auch kein
     * etabliertes Modell — dort entscheidet weiter der Haken, und der vererbt
     * „kumulativ" von Projekt zu Projekt (Vorfall 6172).
     */
    const uncoveredPriorBillingByProject = useMemo(() => {
        // Gerechnet wird in pickerProjectRows (uncoveredPriorBillingForRows,
        // reine Funktion, dort getestet) — mit derselben Funktion, die den
        // fertigen Beleg beim Ausstellen sperrt. Hier nur die Abhaengigkeiten.
        return uncoveredPriorBillingForRows({
            projectRows,
            priorBillingMode,
            pickerInvoices,
            externalAdvances,
            docType,
            periodTo,
            includeUndated,
        });
    }, [
        projectRows,
        priorBillingMode,
        pickerInvoices,
        externalAdvances,
        docType,
        periodTo,
        includeUndated,
    ]);

    /**
     * Der Zustand JEDES gelisteten Projekts — die eine Ableitung, aus der der
     * Dialog ueberall liest.
     *
     * Vorher wurde resolveBillingMode an drei Stellen unabhaengig neu
     * gerechnet: fuer die Ansage an der Zeile (einmal je Zeile, bei
     * zweihundert Projekten zweihundertmal je Durchlauf), fuer die Rueckfrage
     * nach uebergangenen Vormonaten und beim Anlegen des Belegs. Drei
     * Ableitungen desselben Sachverhalts laufen frueher oder spaeter
     * auseinander — und die Stelle, an der sie es tun, entscheidet ueber den
     * Belegbetrag.
     *
     * Massgeblich sind ausschliesslich `priorBillingMode` (das Modell der
     * ausgestellten Belege) und der Deckungsbefund. NICHT die Belegzahl aus
     * documentTallyByProject: die zaehlt jede Belegart mit Nummer, und ein
     * Projekt mit ausschliesslich Abschlagsrechnungen haette dort Belege, aber
     * kein Modell (Begruendung in billingMode.ts).
     */
    const billingStanceByProject = useMemo(
        () =>
            // Welcher Ladezustand der Deckungspruefung als bekannt gilt, steht
            // in pickerProjectRows (billingStancesForRows, dort getestet).
            billingStancesForRows({
                projectRows,
                priorBillingMode,
                uncoveredPriorBillingByProject,
                coverageLoad,
            }),
        [projectRows, priorBillingMode, uncoveredPriorBillingByProject, coverageLoad],
    );

    /**
     * Der Zustand eines Projekts, mit Rueckfall.
     *
     * 'open' ist der richtige Rueckfall: ein Projekt, ueber das nichts bekannt
     * ist, hat kein Modell — und dann wird gefragt, statt eines zu behaupten.
     */
    const stanceFor = (projectId: string): BillingStance =>
        billingStanceByProject.get(projectId) ?? OPEN_BILLING_STANCE;

    /**
     * Projektbestand neu laden, nachdem am Leistungsprotokoll geschrieben
     * wurde — nach dem Nachtrag der Protokolluecke (einzeln und als Stapel)
     * und nach der Buchung einer Monatspauschale.
     *
     * EIN Helfer fuer alle drei Stellen, weil sie sonst auseinanderlaufen:
     * zwei schluckten einen Ladefehler, der einzelne Nachtrag nicht — dort
     * meldete ein bloss fehlgeschlagenes Neuladen "nicht gespeichert". Der
     * Helfer wirft nie (refreshProjectList); die Liste bleibt dann auf dem
     * Stand von vorhin.
     */
    const refreshProjects = useCallback(
        () =>
            refreshProjectList({
                invalidate: invalidateProjectsLoadCache,
                load: loadProjectsFromCloud,
                setProjects,
            }),
        [],
    );

    // Nachtrag der Protokolluecke: Zustand, Auswahl der Zeilen und die beiden
    // Schreibwege (useBacklogDating, Regeln in backlogDatingLogic). Hier und
    // nicht oben bei den uebrigen Zustaenden, weil er an projectRows haengt.
    const {
        setBacklogDates,
        backlogBusy,
        backlogNote,
        backlogError,
        obviousBusy,
        undatedTotal,
        backlogDateFor,
        autoDatableRows,
        manualUndatedRows,
        handleDateObviousRows,
        handleDateBacklog,
    } = useBacklogDating({ projectRows, today, userProfile, refreshProjects });

    /** Projekte, an denen tatsaechlich etwas offen ist — alles andere waere Rauschen. */
    const billableProjects = useMemo(
        () => billableRows(projectRows),
        [projectRows],
    );

    /**
     * Der Betrag, auf den es bei DIESER Belegart ankommt — die Regel steht in
     * pickerProjectRows (relevantNetCentsFor, Begruendung dort).
     */
    const relevantNetCents = (row: (typeof projectRows)[number]): number =>
        relevantNetCentsFor(row, docType);

    // Grundlage, Betragsfilter und Suche der Liste stehen in pickerProjectRows
    // (filterPickerProjects, reine Funktion, Begruendung und Tests dort). Die
    // Unterdrueckung der Abhaengigkeitswarnung ist entfallen: die Regel liest
    // docType jetzt als Argument statt ueber relevantNetCents, die Liste der
    // Abhaengigkeiten ist damit vollstaendig.
    const pickerProjects = useMemo(() => {
        return filterPickerProjects({ projectRows, billableProjects, docType, projectSearch });
    }, [projectRows, billableProjects, docType, projectSearch]);

    /** Was in der Liste steht, in einer Zahl — sonst scrollt man blind. */
    const pickerTotalNetCents = useMemo(
        () => totalRelevantNetCents(pickerProjects, docType),
        [pickerProjects, docType],
    );

    /**
     * Der angefangene Anlagevorgang — EIN Zustand fuer die ganze Rueckfrage-
     * Kette.
     *
     * Vorher stand hier je Rueckfrage ein eigener Zustand (Abrechnungsart,
     * Vormonate, Kunde), und jeder trug seine eigene Kopie dessen, was bis
     * dahin entschieden war. Drei Zustaende, die dieselbe Sache halb kennen,
     * laufen bei der naechsten Frage auseinander — und die Reihenfolge, in der
     * sie sich gegenseitig aufrufen, stand nirgends geschrieben.
     *
     * Jetzt: das geklickte Projekt und die bisher gegebenen Antworten. WELCHE
     * Frage als naechstes dran ist, rechnet invoicePickerFlow aus — dort ist es
     * geprueft. Geschrieben wird waehrend der Kette nichts; Abbrechen setzt
     * diesen Zustand auf null und laesst nichts zurueck.
     */
    const [flow, setFlow] = useState<{
        entry: (typeof projectRows)[number];
        answers: PickerAnswers;
        /**
         * Die Grundlage der Kette, EINGEFROREN beim Start.
         *
         * Frueher wurde sie bei jedem Neuzeichnen aus dem Dialogzustand neu
         * abgeleitet. Aendert sich dabei etwas, aus dem der Plan folgt — die
         * Belegart, der Zeitraum, ein nebenher neu geladener Belegbestand —,
         * dann faellt der gerade gezeigte Schritt aus dem Plan: `flow` steht,
         * kein Schritt zeichnet, alle Projektzeilen bleiben gesperrt. Der
         * Dialog wirkte eingefroren. Die Kette, die man begonnen hat, ist jetzt
         * die Kette, die man beendet.
         *
         * Einzige Ausnahme ist `hasCustomer`: den setzt der Kundenschritt
         * selbst, denn er beantwortet sich durch seine Wirkung.
         */
        facts: PickerStepFacts;
    } | null>(null);

    /**
     * Der laufende Vorgang, auch fuer Rueckrufe, die einen `await` ueberlebt
     * haben.
     *
     * Ein in der Ereignisbehandlung eingefangenes `flow` ist der Stand VOR dem
     * Warten. Wer danach damit weiterarbeitet, arbeitet an einem Vorgang, den
     * es womoeglich nicht mehr gibt.
     */
    const flowRef = useRef<typeof flow>(null);
    flowRef.current = flow;
    // Beim Abhaengen der Ansicht loslassen. Ohne dieses Aufraeumen bliebe der
    // Ref auf dem letzten Vorgang stehen, und ein Rueckruf, der das Abhaengen
    // ueberlebt (Kundenzuweisung mitten im Schreiben, dann Modulwechsel), kaeme
    // an seinem eigenen Riegel vorbei: der Vergleich ginge auf, und der Beleg
    // entstuende doch.
    useEffect(
        () => () => {
            flowRef.current = null;
        },
        [],
    );

    /**
     * Wer darf die Kunden-Zuordnung eines Projekts setzen? Ein Schritt der
     * Rechnungsanlage — Recht „Rechnungen bearbeiten" (rechnungRechte). Die
     * Zuweisung schreibt am Projekt (projects.customer_id), nicht am
     * Kundenstamm; das haengt am Recht, nicht am Weg hierher.
     */
    const canAssignCustomer = darf.kundeZuordnen.erlaubt;

    const [assigningCustomer, setAssigningCustomer] = useState(false);
    const [assignCustomerError, setAssignCustomerError] = useState<string | null>(null);

    /**
     * Der Fokus liegt beim Oeffnen im Suchfeld.
     *
     * Erster Handgriff war bisher ausnahmslos die Maus, obwohl fast jeder
     * Vorgang mit dem Eintippen einer Projektnummer beginnt. Der Inhalt wird
     * gleich mit ausgewaehlt: kommt der Dialog aus der Buchhaltung, steht die
     * Projektnummer schon drin (openPickerForProject) — ausgewaehlt laesst sie
     * sich ueberschreiben, ohne sie erst zu loeschen.
     *
     * Ueber einen Timer und nicht direkt: der Dialog wird im selben Durchlauf
     * erst montiert, das Feld gibt es zum Zeitpunkt des Effekts noch nicht in
     * jedem Fall.
     */
    useEffect(() => {
        if (!pickerOpen) return;
        const timer = window.setTimeout(() => {
            pickerSearchRef.current?.focus();
            pickerSearchRef.current?.select();
        }, 0);
        return () => window.clearTimeout(timer);
    }, [pickerOpen]);

    /**
     * Escape schliesst den Anlagedialog.
     *
     * Zwei Faelle bleiben ausgenommen, und beide aus demselben Grund — Escape
     * darf nur den obersten Vorgang treffen:
     *  - Steht eine Rueckfrage davor (Kundenzuweisung, uebergangene Vormonate),
     *    gehoert die Taste ihr; der Dialog dahinter waere sonst weg, waehrend
     *    die Frage noch offen ist.
     *  - Laeuft gerade ein Anlegen oder Oeffnen, wird nicht geschlossen: der
     *    Beleg entsteht bereits, und der Dialog schliesst sich danach ohnehin
     *    selbst.
     */
    useEffect(() => {
        // Auch bei geschlossenem Anlagedialog, solange eine Kette laeuft: die
        // Schritte werden ausserhalb von `pickerOpen` gezeichnet, und ein hier
        // ersatzlos ausgestiegener Griff liesse den Nutzer vor einem Fenster
        // ohne Escape stehen.
        if (!pickerOpen && !flow) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            // Escape gehoert dem obersten Vorgang. Bei laufender Rueckfrage-
            // Kette ist das die Kette: sie bricht ab, der Anlagedialog bleibt
            // stehen. Geschrieben wurde bis hierhin nichts, es bleibt also auch
            // nichts zurueck. Frueher stieg der Griff hier ersatzlos aus, und
            // weil kein Schrittdialog einen eigenen registriert, war Escape
            // waehrend der ganzen Kette tot.
            if (flow) {
                // Waehrend die Kundenzuweisung laeuft NICHT: sie schreibt am
                // Projekt, und der Rueckruf danach muesste sonst mitten im Zug
                // merken, dass sein Vorgang weg ist. Der Riegel steht trotzdem
                // auch dort (assignCustomerAndContinue) — hier ist er nur der
                // billigere Weg.
                if (assigningCustomer) return;
                setFlow(null);
                setAssignCustomerError(null);
                return;
            }
            if (creating != null || opening != null) return;
            closePicker();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [pickerOpen, flow, assigningCustomer, creating, opening, closePicker]);

    /**
     * Pfeiltasten durch die Projektliste.
     *
     * Gesucht wird im DOM statt ueber einen gehaltenen Index: welche Zeilen es
     * gibt, entscheidet die Suche, und ein Index waere nach jedem Tastendruck
     * eine Zeile zu alt. Die Rechnung selbst — welcher Index als naechstes dran
     * ist — liegt in listKeyboardNav und ist dort geprueft.
     */
    const movePickerFocus = (key: ListNavKey) => {
        const found = pickerListRef.current?.querySelectorAll<HTMLButtonElement>(
            'button[data-picker-row]:not([disabled])',
        );
        const rows: HTMLButtonElement[] = found ? Array.from(found) : [];
        const current = rows.findIndex((btn) => btn === document.activeElement);
        const next = nextListIndex(current, rows.length, key);
        // -1 heisst: zurueck ins Suchfeld. Sonst kaeme man mit der Tastatur in
        // die Liste hinein, aber nicht mehr zum Tippen zurueck.
        if (next < 0) {
            pickerSearchRef.current?.focus();
            return;
        }
        rows[next]?.focus();
    };

    /**
     * Legt den Beleg an.
     *
     * `answers` sind die Antworten der Rueckfrage-Kette und stehen beim Aufruf
     * vollstaendig fest — die Kette ist durch, sonst waere diese Funktion nicht
     * an der Reihe. Bewusst ein Argument und kein Dialogzustand: ein
     * projektuebergreifender Schalter war die Ursache von Vorfall 6172, und ein
     * Vorgabewert waere derselbe Schalter mit unsichtbarem Griff.
     *
     * `answers.overrideFrom` zieht den Zeitraum fuer DIESEN einen Beleg nach
     * vorn, wenn der Nutzer Vormonate einbezogen hat. Der im Anlagedialog
     * gewaehlte Zeitraum gilt weiter fuer das naechste Projekt.
     */
    const handleCreateFromProject = async (
        entry: (typeof projectRows)[number],
        givenAnswers: PickerAnswers,
        facts: PickerStepFacts,
    ) => {
        // Nur, was die Kette fuer DIESES Projekt auch gefragt hat. Eine Antwort,
        // deren Schritt durch ein „zurueck" aus dem Plan gefallen ist, darf den
        // Beleg nicht mehr veraendern (Begruendung in invoicePickerFlow).
        //
        // Gerechnet mit der EINGEFROHRENEN Grundlage der Kette, nicht mit einer
        // frisch abgeleiteten: sonst entschiede der Stand von jetzt darueber,
        // welche der damals gegebenen Antworten noch gelten.
        const answers = effectivePickerAnswers(facts, givenAnswers);
        // Ohne Zuruecksetzen bliebe die Meldung des vorigen Versuchs im Dialog
        // stehen und sähe aus, als beträfe sie das gerade gewählte Projekt.
        setError(null);
        // Was gilt und was an die Dienste geht, entscheidet invoiceCreateFlow
        // (dort ohne React geprueft). Hier bleibt: den Zustand einsammeln und
        // das Ergebnis anwenden — mit denselben Settern in derselben
        // Reihenfolge wie zuvor.
        const prepared = prepareInvoiceCreate({
            entry,
            answers,
            stance: facts.stance,
            docType,
            periodFrom,
            periodTo,
            includeUndated,
            pickerInvoices,
            invoices,
            canAssignCustomer,
            invoiceConfig,
            today,
        });
        if (prepared.kind === 'error') {
            setError(prepared.error);
            return;
        }
        setCreating(prepared.plan.projectId);
        try {
            const outcome = await createInvoiceFromProject(prepared.plan, {
                fetchProjectSplit,
                buildProjectServiceLines,
                createAdvanceInvoice,
                createSplitAdvanceInvoices,
                createFinalInvoice,
                createInvoiceDraft,
                createSplitInvoiceDrafts,
            });
            // Sperre oder Fehler: Meldung setzen, Dialog und Kette bleiben
            // offen — wie bei den fruehen Ausstiegen zuvor.
            if (outcome.kind === 'error') {
                setError(outcome.error);
                return;
            }
            const { drafts, open, notice } = outcome;
            closePicker();
            setInvoices((prev) => [...drafts, ...prev]);
            setEditing(open);
            if (notice !== null) setNotice(notice);
        } catch (e: unknown) {
            // Die Fehler der Dienste bildet createInvoiceFromProject selbst ab.
            // Das hier faengt nur noch das Anwenden des Ergebnisses — auch das
            // stand frueher im selben catch und bekam dieselbe Meldung.
            setError(describeCreateFailure(e, entry.undated).error);
        } finally {
            setCreating(null);
        }
    };

    /**
     * Was ueber ein Projekt bekannt ist, wenn die Rueckfrage-Kette geplant wird.
     *
     * Alle vier Angaben kommen aus bereits geladenen Daten — kein zusaetzlicher
     * Abruf, und keine zweite Ableitung neben denen, die die Liste ohnehin
     * fuehrt.
     */
    const pickerFactsFor = (entry: (typeof projectRows)[number]): PickerStepFacts => ({
        docType,
        stance: stanceFor(entry.project.id),
        deductibleAdvances: deductibleAdvancesByProject.get(entry.project.id) ?? 0,
        // Dieselbe Rechnung wie bisher: ohne gewaehlten Beginn gibt es keine
        // uebergangenen Vormonate, weil nichts uebergangen wird. WELCHE
        // Belegarten und Modelle die Frage ueberhaupt kennen, entscheidet der
        // Plan (invoicePickerFlow) — hier steht nur die Zahl.
        priorOpenMonths: periodFrom ? openMonthsBeforePeriod(entry.months, periodFrom).length : 0,
        hasCustomer: !!entry.project.customer_id,
    });

    /**
     * Der Klick auf ein Projekt startet die Kette.
     *
     * Wer keinen Kunden zuweisen darf, kommt gar nicht erst hinein: die Kette
     * endete sonst vor einem Auswahlfenster, das er nicht bedienen kann.
     */
    const handleProjectClick = (entry: (typeof projectRows)[number]) => {
        setError(null);
        // Die Grundlage wird EINMAL gelesen und dann festgehalten — siehe
        // `flow.facts`.
        const facts = pickerFactsFor(entry);
        // Solange die Deckungspruefung laeuft, waere „kumulativ" eine Wahl ins
        // Ungewisse — und nicht mehr einzufangen, sobald der Beleg da ist. Die
        // Zeile ist waehrenddessen ohnehin gesperrt; das hier ist der zweite
        // Riegel fuer den Weg ueber die Tastatur.
        if (awaitsCoverageCheck(facts.stance, docType)) return;
        if (!entry.project.customer_id && !canAssignCustomer) {
            setError(missingCustomerMessage(entry.project.name, { canAssign: false }));
            return;
        }
        // Der Entwurf der Abschlagshoehe gehoert zu GENAU EINEM Projekt. Ohne
        // dieses Zuruecksetzen stuende der Betrag des vorigen im Feld — und die
        // Vorschau bestaetigte ihn auch noch.
        if (advanceDraft.projectId !== entry.project.id) {
            setAdvanceDraft({ projectId: entry.project.id, choice: EMPTY_ADVANCE_DRAFT });
        }
        runPickerFlow(entry, EMPTY_PICKER_ANSWERS, facts);
    };

    /**
     * Einen Schritt weiter — oder, wenn keiner mehr offen ist, den Beleg anlegen.
     *
     * Bewusst hier und nicht in einem Effekt: ein Effekt auf „Kette durch"
     * liefe bei jedem Neuzeichnen erneut an und legte den Beleg zweimal an. Der
     * Uebergang gehoert an die Stelle, an der die Antwort ankommt.
     */
    const runPickerFlow = (
        entry: (typeof projectRows)[number],
        answers: PickerAnswers,
        facts: PickerStepFacts,
    ) => {
        const step = nextPickerStep(facts, answers);
        if (step) {
            setFlow({ entry, answers, facts });
            return;
        }
        setFlow(null);
        void handleCreateFromProject(entry, answers, facts);
    };

    /** Eine Antwort geben und weitergehen. */
    const answerPickerStep = (patch: Partial<PickerAnswers>) => {
        if (!flow) return;
        runPickerFlow(flow.entry, { ...flow.answers, ...patch }, flow.facts);
    };

    /**
     * Einen Schritt zurueck: die Antwort des vorigen Schritts zuruecknehmen.
     *
     * Nur diese eine — die spaeteren bleiben stehen, damit niemand nach einer
     * Korrektur alles erneut beantworten muss (invoicePickerFlow).
     */
    const backPickerStep = () => {
        if (!flow) return;
        const previous = previousPickerStep(flow.facts, flow.answers);
        if (!previous) return;
        setFlow({ ...flow, answers: clearPickerAnswer(previous, flow.answers) });
    };

    /**
     * Der Anlagedialog nimmt keine Eingabe an, solange eine Rueckfrage laeuft.
     *
     * Die Schritte liegen als Overlay darueber, aber ohne Fokusfalle:
     * Shift+Tab fuehrte hinaus, und dort standen Belegart, Zeitraum, Suche und
     * das Schliessen-X offen. Zwei belegte Folgen: die Belegart mitten in der
     * Kette umzuschalten fuehrte in einen Zustand, in dem kein Schritt mehr
     * zeichnete (dagegen steht jetzt zusaetzlich die eingefrorene Grundlage der
     * Kette), und der Zeitraum wurde beim Anlegen live gelesen — die Antworten
     * haetten dann fuer ein anderes Fenster gegolten als das, zu dem sie
     * gegeben wurden.
     *
     * `disabled` statt einer Fokusfalle: es nimmt die Bedienelemente auch aus
     * der Tabreihenfolge, ist an jedem einzelnen ablesbar und braucht keinen
     * Griff, der bei jedem Tastendruck den Fokus zurueckholt.
     */
    const pickerLocked = flow != null;

    /** Der Schritt, der gerade dran ist — `null`, wenn keine Kette laeuft. */
    const currentPickerStep: PickerStepId | null = flow
        ? nextPickerStep(flow.facts, flow.answers)
        : null;

    /** „Schritt 2 von 3" fuer den gerade gezeigten Schritt. */
    const currentStepPosition = (step: PickerStepId) =>
        flow ? pickerStepPosition(flow.facts, flow.answers, step) : { position: 0, total: 0 };

    /** Steht hinter dem aktuellen Schritt noch ein frueherer? */
    const hasPreviousStep = (): boolean =>
        flow != null && previousPickerStep(flow.facts, flow.answers) != null;

    /**
     * Kunde zuweisen und den angefangenen Beleg weiterlaufen lassen.
     *
     * Geschrieben wird ausschliesslich ueber setProjectCustomer: es fasst nur
     * projects.customer_id an, nicht data — sonst ueberschriebe die Zuweisung
     * einen parallel offenen Berichtsstand.
     *
     * Danach kein Neuladen: die Projektzeile bekommt die neue customer_id in
     * den Zustand, und derselbe Aufruf laeuft mit einer Kopie der Zeile weiter.
     * Ein Refetch waere hier nicht nur langsam, er wuerde den Zeitraum und die
     * gewaehlte Belegart hinter dem Dialog neu aufbauen.
     */
    const assignCustomerAndContinue = async (customer: Customer) => {
        const pending = flow;
        if (!pending) return;
        const project = pending.entry.project;
        setAssigningCustomer(true);
        setAssignCustomerError(null);
        try {
            await setProjectCustomer(project.id, customer.id);
        } catch (e) {
            setAssignCustomerError(rechtFehlerOder(e));
            setAssigningCustomer(false);
            return;
        }
        setAssigningCustomer(false);
        // Die Zuweisung steht am Projekt und bleibt bestehen — sie ist die
        // dokumentierte Ausnahme (invoicePickerFlow.ts). Der Zustand der
        // Ansicht wird deshalb in JEDEM Fall nachgezogen, auch wenn der Vorgang
        // inzwischen abgebrochen wurde.
        setProjects((prev) =>
            prev.map((p) => (p.id === project.id ? { ...p, customer_id: customer.id } : p)),
        );
        // Der Editor sucht den Rechnungsempfaenger in dieser Liste. Fehlte er
        // dort, staende im Beleg "Kunde unbekannt", obwohl er gerade gewaehlt
        // wurde.
        // Dieselbe Regel wie ueberall, aus einer Hand: ersetzen, wenn bekannt,
        // anhaengen nur, wenn nicht archiviert.
        setCustomers((prev) => mergeCustomerIntoList(prev, customer));

        // Laeuft der Vorgang ueberhaupt noch? Zwischen dem Klick auf den Kunden
        // und dieser Zeile liegt ein Schreibzugriff auf den Server, und in
        // dieser Zeit kann der Nutzer abgebrochen (Escape, Abbrechen, Dialog
        // geschlossen) oder ein anderes Projekt angeklickt haben. `pending` ist
        // der Stand von VOR dem Warten; massgeblich ist der jetzige, und den
        // haelt der Ref. Ohne diesen Vergleich entstand der Beleg trotz
        // Abbruch, und der Editor sprang auf — die Zusage „Abbrechen laesst
        // nichts zurueck" war an genau dieser Stelle unwahr.
        if (flowRef.current !== pending) {
            setNotice(
                `Der Kunde „${customer.name}" ist dem Projekt jetzt zugeordnet. Ein Beleg wurde nicht angelegt — der Vorgang war bereits abgebrochen.`,
            );
            return;
        }

        // Zurueck in die Kette, mit der ergaenzten Zeile: der Kundenschritt
        // faellt damit aus dem Plan, und weil er der letzte ist, entsteht der
        // Beleg unmittelbar danach. `hasCustomer` ist die einzige Angabe der
        // eingefrorenen Grundlage, die sich waehrend der Kette aendern darf —
        // dieser Schritt beantwortet sich durch seine Wirkung.
        runPickerFlow(
            { ...pending.entry, project: { ...project, customer_id: customer.id } },
            pending.answers,
            { ...pending.facts, hasCustomer: true },
        );
    };

    /**
     * Aus der Buchhaltung heraus eine Rechnung fuer dieses Projekt anlegen.
     *
     * Der Anlagedialog oeffnet sich sofort und ist auf das Projekt eingegrenzt
     * — bei zweihundert Projekten waere es sonst dasselbe Projekt ein zweites
     * Mal zu suchen. Die Belegart und der Zeitraum bleiben ausdruecklich eine
     * Entscheidung im Dialog: aus ihnen folgt, WIE die Positionen entstehen,
     * und die darf kein Knopf im Vorbeigehen treffen.
     */
    const openPickerForProject = (projectId: string) => {
        const project = projects.find((p) => p.id === projectId);
        setSection('documents');
        setProjectSearch(project?.project_number || project?.name || '');
        // Belegart passend zum Projekt, statt die Vorliebe der letzten Sitzung
        // weiterzureichen. Zwei Gruende, und beide waren gemeldete Fehlgriffe:
        //
        //  - Ein abgeschlossenes Projekt bekommt die SCHLUSSRECHNUNG. Sie zieht
        //    die gestellten Abschlaege ab; als "Rechnung" angelegt fehlte der
        //    Abzugsblock.
        //  - Stand zuletzt "Abschlagsrechnung", blieb der Leistungszeitraum im
        //    Dialog unsichtbar — der Abschlag kennt keinen. Wer aus der
        //    Buchhaltung auf "Rechnung erstellen" klickt, wurde also gar nicht
        //    nach dem Zeitraum gefragt.
        //
        // Vorbelegung, keine Festlegung: im Dialog bleibt jede Belegart
        // waehlbar (siehe billingDocTypeForProject).
        setDocType(billingDocTypeForProject(project));
        openPicker();
    };

    const handleChanged = (updated: Invoice) => {
        setInvoices((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
        setEditing(updated);
    };

    if (editing) {
        return (
            <InvoiceEditor
                invoice={editing}
                customer={customers.find((c) => c.id === editing.customerId)}
                // Die geladenen Projekte, damit die Maske auch bei einem Beleg
                // ohne Projektvermerk Nummer und Name zeigen kann.
                projects={projects}
                invoiceConfig={invoiceConfig}
                company={company}
                quoteConfig={quoteConfig}
                userProfile={userProfile}
                today={today}
                onChange={handleChanged}
                // Nur die Kundenliste nachziehen — `editing` bleibt unberuehrt.
                // Wuerde der Beleg mitgesetzt, bekaeme die invoice-Prop eine
                // neue Identitaet und der Editor verwuerfe seinen Entwurf.
                onCustomerChange={(updated) =>
                    // Ueber mergeCustomerIntoList und nicht von Hand: ein bis
                    // dahin unbekannter, ARCHIVIERTER Kunde darf hier nicht
                    // angehaengt werden, sonst stuende er anschliessend in den
                    // Auswahllisten (fetchCustomers blendet ihn aus).
                    setCustomers((prev) => mergeCustomerIntoList(prev, updated))
                }
                onBack={(editorNotice) => {
                    // Aus der Kundenakte angesprungen: Zurueck fuehrt dorthin.
                    // Mit Meldung (Storno, Korrektur, Gutschrift) bleibt die
                    // Liste stehen — dort steht der neue Beleg, und die Meldung
                    // ginge beim Ansichtswechsel verloren.
                    const angesprungen = rueckwegInvoiceIdRef.current === editing.id;
                    const zurueck = !editorNotice && angesprungen && onCloseInitialInvoice;
                    rueckwegInvoiceIdRef.current = null;
                    // Der Sprung kam nicht aus der Liste: im Hintergrund steht
                    // der zuletzt gemerkte Reiter, evtl. die Buchhaltung — dort
                    // erschienen weder der neue Beleg noch die Meldung.
                    if (editorNotice && angesprungen) setSection('documents');
                    setEditing(null);
                    if (zurueck) {
                        // Von Hand: die Ansicht wird gleich demontiert, der
                        // Effekt auf `editing` liefe nicht mehr — der naechste
                        // Aufruf der Rechnungen oeffnete sonst diesen Beleg.
                        rememberOpen(null);
                        zurueck();
                        return;
                    }
                    if (editorNotice) setNotice(editorNotice);
                    void load();
                }}
                onOpenProject={onOpenProject}
            />
        );
    }

    // Buchhaltung ist kein eigenes Thema: sie zeigt denselben Vorgang von der
    // anderen Seite — was im Projekt abgerechnet wurde und was davon offen
    // blieb. Als eigener Reiter unter Verwaltung stand sie neben dem Modul,
    // das sie erzeugt.
    if (sichtbarerAbschnitt === 'accounting') {
        return (
            <div ref={accountingScrollRef} data-glass-view="rechnungen" className="h-full overflow-y-auto p-4 md:p-6 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <FileText className="w-6 h-6" /> Rechnungen
                    </h1>
                    {/* Kontext-"?" auch im Abrechnungszweig: App.tsx blendet
                        das schwebende Fragezeichen fuer 'invoices' aus, sonst
                        stuende hier gar keine Hilfe. */}
                    {onOeffneLernzentrum && (
                        <KontextHilfe view="invoices" onOeffneLernzentrum={onOeffneLernzentrum} variant="inline" />
                    )}
                </div>
                <SectionTabs section={section} onChange={setSection} />
                <AdminAccountingView
                    projects={projects}
                    currentUser={userProfile}
                    onRefresh={() => void load()}
                    onOpenProject={onOpenProject}
                    initialProjectId={initialAccountingProjectId}
                    onBillProject={openPickerForProject}
                    // Ohne „Rechnungen bearbeiten“ nur lesend (rechnungRechte).
                    nurLesen={buchhaltungNurLesen(rechte)}
                    // Die Grundeinstellung der FIRMA, nicht die des eigenen
                    // Profils — die traegt sie nur beim Admin.
                    defaultTaxRateBp={invoiceConfig.defaultTaxRateBp}
                />
            </div>
        );
    }

    return (
        // h-full + overflow-y-auto wie im Abrechnungs-Zweig: die App-Schale
        // (<main> in App.tsx) traegt overflow-hidden, also muss die Ansicht
        // ihren eigenen Scrollbereich bilden. Ohne das war alles unterhalb der
        // Fensterhoehe schlicht abgeschnitten und nicht erreichbar.
        <div ref={documentsScrollRef} data-glass-view="rechnungen" className="h-full overflow-y-auto p-4 md:p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                    <FileText className="w-6 h-6" /> Rechnungen
                </h1>
                <div className="flex items-center gap-2">
                    {/* Ohne Recht gesperrt, mit dem fehlenden Recht im Titel
                        (rechnungRechte) — nicht ausgeblendet. */}
                    <Button
                        variant="secondary"
                        onClick={() => setRunOpen(true)}
                        disabled={!darf.rechnungslauf.erlaubt}
                        title={darf.rechnungslauf.hinweis ?? 'Mehrere Projekte auf einmal abrechnen'}
                    >
                        <Layers className="w-4 h-4" />
                        <span className="ml-2 hidden sm:inline">Rechnungslauf</span>
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => setDatevOpen(true)}
                        disabled={!darf.datevExport.erlaubt}
                        title={darf.datevExport.hinweis ?? 'Buchungsstapel für DATEV oder Simba erzeugen'}
                    >
                        <FileSpreadsheet className="w-4 h-4" />
                        <span className="ml-2 hidden sm:inline">Steuerberater</span>
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => setBankOpen(true)}
                        disabled={!darf.bankImport.erlaubt}
                        title={
                            darf.bankImport.hinweis ??
                            'Kontoauszug oder Umsatzliste (CAMT-XML) hochladen und Zahlungen zuordnen'
                        }
                    >
                        <Landmark className="w-4 h-4" />
                        <span className="ml-2 hidden sm:inline">Kontoauszug</span>
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => setSweepOpen(true)}
                        title="Alle Projekte gegen die 19 Regeln prüfen, ohne etwas zu ändern"
                    >
                        <ShieldAlert className="w-4 h-4" />
                        <span className="ml-2 hidden sm:inline">Projektcheck</span>
                    </Button>
                    {/* Auch waehrend ein Beleg geoeffnet wird gesperrt: das
                        Neuladen ersetzt die Liste, unter der openInvoice
                        gerade seinen Beleg sucht — es verpuffte ohne
                        sichtbare Wirkung und liesse den Klick von vorhin
                        folgenlos aussehen. */}
                    <Button
                        variant="secondary"
                        onClick={() => void load()}
                        disabled={loading || opening !== null}
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Aktualisieren
                    </Button>
                    {/* Ebenfalls gegen `opening` gesperrt: loest der Beleg
                        auf, ersetzt der Editor die ganze Liste — der eben
                        geoeffnete Anlagedialog waere weg, und nach der
                        Rueckkehr stuende er wieder da, ohne dass jemand ihn
                        angefordert haette. */}
                    <Button
                        onClick={openPicker}
                        disabled={opening !== null || !darf.neueRechnung.erlaubt}
                        title={darf.neueRechnung.hinweis}
                    >
                        <Plus className="w-4 h-4" /> Neue Rechnung
                    </Button>
                    {/* Kontext-"?" ganz rechts, hinter den Werkzeugen: in
                        dieser Ansicht ersetzt es das schwebende Fragezeichen
                        aus App.tsx. */}
                    {onOeffneLernzentrum && (
                        <KontextHilfe view="invoices" onOeffneLernzentrum={onOeffneLernzentrum} variant="inline" />
                    )}
                </div>
            </div>

            {/* Solange der Projekt-Picker offen ist, liegt dieses Banner hinter dem
                abgedunkelten Overlay — der Fehler wird dann im Dialog gezeigt. */}
            {error && !pickerOpen && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span className="flex-1">{error}</span>
                    <button onClick={() => setError(null)} className="text-red-600 hover:underline">
                        schließen
                    </button>
                </div>
            )}

            {notice && (
                <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                    <span className="flex-1">{notice}</span>
                    <button
                        onClick={() => setNotice(null)}
                        className="text-emerald-700 hover:underline"
                    >
                        schließen
                    </button>
                </div>
            )}

            {/*
              Kennzahlen aus invoiceStats. Die Leiste haelt Stichtagsgroessen
              (Bestand, immer heute) und Flussgroessen (Zeitraum) sichtbar
              auseinander — ohne diese Trennung liest man die offenen
              Forderungen als Zeitraumwert und wundert sich, dass sie sich beim
              Umschalten nicht aendern.
            */}
            {paymentsError && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{paymentsError}</span>
                </div>
            )}

            <SectionTabs
                section={sichtbarerAbschnitt}
                onChange={setSection}
                buchhaltungGesperrt={darf.buchhaltung.hinweis}
            />

            {/* paymentsPending haelt die zahlungsabhaengige Angabe zurueck:
                ohne Zahlungen gilt kein Beleg als beglichen, und ein
                Zahlungseingang "nach 0 Tagen" waere schlicht falsch. Die
                uebrigen Kacheln rechnen ohne Zahlungen — offene Forderungen,
                ueberfaellig, Entwuerfe, Guthaben und auch "vereinnahmt" stehen
                als Summen auf der Belegzeile selbst. */}
            <InvoiceKpiBar
                stats={stats}
                periodLabel={periodLabel}
                onFilter={setFilter}
                onIssuedBreakdown={() => setIssuedOpen((v) => !v)}
                issuedBreakdownOpen={issuedOpen}
                paymentsPending={paymentsPending}
            />

            {issuedOpen && (
                <IssuedBreakdownPanel
                    points={issuedBreakdown}
                    granularity={issuedGranularity}
                    onGranularityChange={setIssuedGranularity}
                    onClose={() => setIssuedOpen(false)}
                />
            )}

            <button
                type="button"
                onClick={() => setChartsOpen((v) => !v)}
                className="self-start text-sm text-slate-600 hover:text-slate-900 hover:underline"
            >
                {chartsOpen ? 'Auswertung ausblenden' : 'Auswertung einblenden'}
            </button>

            {chartsOpen && (
                <InvoiceCharts
                    monthly={charts.monthly}
                    aging={charts.aging}
                    topCustomers={charts.topCustomers}
                    paymentBehaviour={charts.paymentBehaviour}
                    periodLabel={periodLabel}
                    // Die Zahlungsmoral ist das einzige Panel, das an den
                    // Zahlungen haengt. Ohne sie bliebe die Tabelle leer und
                    // meldete "keine Rechnung vollstaendig bezahlt" — eine
                    // Aussage ueber die Kunden, wo bloss die Daten fehlen.
                    paymentsPending={paymentsPending}
                />
            )}

            <InvoiceListFilterBar
                search={search}
                onSearchChange={setSearch}
                filter={filter}
                onFilterChange={setFilter}
                customerFilter={customerFilter}
                onCustomerFilterChange={setCustomerFilter}
                customerOptions={customerOptions}
                typeFilter={typeFilter}
                onTypeFilterChange={setTypeFilter}
                dateFrom={dateFrom}
                onDateFromChange={setDateFrom}
                dateTo={dateTo}
                onDateToChange={setDateTo}
                filtersActive={filtersActive}
                onResetFilters={resetFilters}
            />

            <InvoiceListTable
                loading={loading}
                invoiceCount={invoices.length}
                visibleCount={visible.length}
                pageItems={pageItems}
                totals={visibleTotals}
                filtersActive={filtersActive}
                onResetFilters={resetFilters}
                sort={sort}
                onSort={setSort}
                opening={opening}
                onOpenInvoice={openInvoice}
                onMarkPaid={setMarkPaid}
                zahlungen={darf.zahlungen}
                customerNameOf={customerNameOf}
                projectLabelOf={projectLabelOf}
                today={today}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                currentPage={currentPage}
                pageCount={pageCount}
                onPageChange={setPage}
            />

            {runOpen && (
                <BillingRunWizard
                    invoiceConfig={invoiceConfig}
                    today={today}
                    customers={customers}
                    onClose={() => setRunOpen(false)}
                    onCreated={() => void load()}
                />
            )}

            {/* Ohne invoices-Prop: der Dialog holt die Belege MIT Positionen
                selbst. Der Zustand hier traegt `lines: []`, und der
                DATEV-Buchungstext verloere damit still die Projektnummern der
                Belege ohne Projektvermerk. */}
            {datevOpen && (
                <DatevExportDialog
                    datevConfig={invoiceConfig.datev}
                    /* Kopfzeile Feld 9 „Exportiert von“ — der NAME des
                       Auslösenden, nicht seine id. Dort stand bisher
                       userProfile.id, also eine auf die Feldlänge
                       abgeschnittene UUID; in der Kanzlei ist eine Kennung
                       wertlos, die dort niemand auflösen kann. Fehlt der Name,
                       bleibt das Feld leer — die Referenzdatei lässt es
                       ebenfalls leer, und leer ist ehrlicher als eine ID. */
                    exportedBy={buildActorName(userProfile) || undefined}
                    onClose={() => setDatevOpen(false)}
                    onExported={async (ids, details) => {
                        // exportedAt wird hier gesetzt, nicht im Dialog: der
                        // Vermerk gehoert erst an den Beleg, wenn die Datei
                        // tatsaechlich beim Nutzer gelandet ist.
                        //
                        // Das Ergebnis geht an den Dialog zurueck, statt in
                        // setError zu landen: der Dialog bleibt offen, die
                        // Meldung laege hinter ihm — und beim zweiten Export
                        // desselben Zeitraums fehlte die Warnung, der Umsatz
                        // wuerde doppelt gebucht.
                        let result: DatevExportMarkResult;
                        try {
                            const { marked, missed, error: markError } =
                                await markInvoicesExported(ids, details.exportedAt);
                            // markedIds IMMER mitgeben, auch im Fehlerfall: die
                            // dort genannten Belege tragen den Vermerk in der
                            // Datenbank, und der Dialog muss sie danach als
                            // exportiert zeigen. Sonst warnte er beim zweiten
                            // Export desselben Zeitraums nicht, obwohl die
                            // Warnung am Beleg schon stuende.
                            result = markError
                                ? {
                                      ok: false,
                                      markedIds: marked,
                                      message: `Der Export wurde erzeugt, konnte aber nur an ${marked.length} von ${ids.length} Belegen vermerkt werden (${
                                          rechtFehlerText({ message: markError }) ?? markError
                                      }). Ein zweiter Export dieses Zeitraums warnt bei den übrigen NICHT.`,
                                  }
                                : missed.length > 0
                                  ? {
                                        ok: false,
                                        markedIds: marked,
                                        message: `Nur ${marked.length} von ${ids.length} Belegen wurden als exportiert vermerkt. Ein zweiter Export dieses Zeitraums warnt bei den übrigen NICHT.`,
                                    }
                                  : { ok: true, markedIds: marked };
                        } catch (e) {
                            // Bleibt fuer den unerwarteten Wurf: welche Belege
                            // dann vermerkt sind, ist nicht bekannt — also
                            // KEINE markedIds behaupten. Ein Abbruch mitten im
                            // Schreiben kann bereits Belege vermerkt haben; der
                            // Dialog darf leere markedIds hier deshalb NICHT als
                            // "keiner" lesen, sondern muss seinen Stand
                            // verwerfen (markStateUnknown).
                            result = {
                                ok: false,
                                markedIds: [],
                                markStateUnknown: true,
                                message: `Der Export wurde erzeugt, der Exportvermerk brach aber ab (${rechtFehlerOder(
                                    e,
                                    () => (e instanceof Error ? e.message : 'unbekannter Fehler'),
                                )}). Welche Belege vermerkt wurden, ist unbekannt — der Stand wird neu aus der Datenbank geladen.`,
                            };
                        }
                        void load();
                        return result;
                    }}
                />
            )}

            {markPaid && (
                <MarkPaidDialog
                    invoice={markPaid}
                    today={today}
                    onClose={() => setMarkPaid(null)}
                    onDone={(updated, payment) => {
                        // Der nachgeladene Beleg traegt den vom Trigger
                        // gesetzten Status — nur er darf in die Liste, ein
                        // lokal fortgeschriebener paidCents waere geraten.
                        setInvoices((prev) =>
                            prev.map((i) => (i.id === updated.id ? updated : i)),
                        );
                        // Die Zahlung noch in den Bestand der Kennzahlen: DSO
                        // und Zahlungsmoral rechnen aus den Zahlungen, nicht aus
                        // den Belegsummen.
                        //
                        // Nur bei stehendem Abruf ('idle') ist der Bestand hier
                        // vollstaendig und darf ergaenzt werden. Waehrend eines
                        // Abrufs gehoert er ihm — und im Fehlerfall ist er leer,
                        // eine einzelne Zahlung darin ergaebe eine Kennzahl aus
                        // genau einem Beleg, waehrend das Banner den Fehlbestand
                        // meldet.
                        if (paymentsLoad === 'idle') {
                            setPayments((prev) =>
                                prev.some((p) => p.id === payment.id) ? prev : [...prev, payment],
                            );
                        }
                        // Und in JEDEM Fall neu abrufen. Ein bereits laufender
                        // Abruf kann seinen Bestand VOR dem Einfuegen gelesen
                        // haben und truege die Zahlung dann nicht — er landete
                        // nach der Buchung und schriebe sie wieder weg. Der
                        // neue Lauf zieht die laufende Nummer hoch und sperrt
                        // die ueberholte Antwort damit aus (createPaymentsStream).
                        // Nach einem Fehlschlag ist derselbe Aufruf zugleich der
                        // Wiederholungsversuch.
                        paymentsStream.load(invoices.map((i) => i.id));
                        setMarkPaid(null);
                        setNotice(
                            `Zahlung über ${formatCents(payment.amountCents)} auf ${
                                updated.invoiceNumber || 'den Beleg'
                            } gebucht.`,
                        );
                    }}
                />
            )}

            {bankOpen && (
                <BankImportDialog
                    today={today}
                    customers={customers}
                    onClose={() => setBankOpen(false)}
                    onImported={load}
                />
            )}

            {sweepOpen && (
                <ProjectCheckSweep
                    projects={projects}
                    config={invoiceConfig.projectCheck}
                    today={today}
                    onClose={() => setSweepOpen(false)}
                    onOpenProject={onOpenProject}
                />
            )}

            {/* Letzter Schritt der Kette: Projekt ohne Kunden. Zuweisen statt
                abbrechen — und bewusst am ENDE, weil er als einziger schreibt.
                Nach ihm folgt keine Frage mehr, in der noch abgebrochen werden
                koennte.

                Der CustomerPickerDialog wird von mehreren Ansichten benutzt und
                bleibt deshalb unveraendert; die Schrittanzeige reicht ueber
                seinen `intro` hinein, damit auch dieser Schritt sagt, wo man
                steht. */}
            {flow && currentPickerStep === 'customer' && (
                <CustomerPickerDialog
                    title="Kunde zuweisen"
                    intro={
                        <>
                            <PickerStepBadge
                                projectName={
                                    flow.entry.project.name ||
                                    flow.entry.project.project_number ||
                                    'Projekt'
                                }
                                step={currentStepPosition('customer')}
                            />
                            Dem Projekt{' '}
                            <strong className="text-slate-700">
                                {flow.entry.project.name || 'ohne Namen'}
                            </strong>{' '}
                            ist kein Kunde zugeordnet — ohne Rechnungsempfänger kann keine Rechnung
                            entstehen. Nach der Zuweisung wird der Beleg direkt weiter angelegt.
                        </>
                    }
                    customers={customers}
                    contextCustomerName={projectContextNames(flow.entry.project).customerName}
                    contextClientName={projectContextNames(flow.entry.project).clientName}
                    saving={assigningCustomer}
                    error={assignCustomerError}
                    onPick={(c) => void assignCustomerAndContinue(c)}
                    onClose={() => {
                        setFlow(null);
                        setAssignCustomerError(null);
                    }}
                />
            )}

            {/* z-[100] ist die Modalebene der Anwendung (Admin, CRM, Freelancer
                nutzen sie ebenso). Sie muss es sein, weil die Seitenleiste als
                `md:relative z-[60]` einen eigenen Stapelkontext aufspannt
                (src/components/Sidebar.tsx): alles darunter — und z-50 lag
                darunter — verschwindet hinter ihr, samt der Abdunklung. Bei
                72rem fiel das nicht auf, weil der Dialog rechts daneben lag. */}
            {pickerOpen && (
                <ProjectPickerDialog
                    creating={creating}
                    opening={opening}
                    pickerLocked={pickerLocked}
                    closePicker={closePicker}
                    error={error}
                    setError={setError}
                    docType={docType}
                    setDocType={setDocType}
                    projectSearch={projectSearch}
                    setProjectSearch={setProjectSearch}
                    periodFrom={periodFrom}
                    setPeriodFrom={setPeriodFrom}
                    periodTo={periodTo}
                    setPeriodTo={setPeriodTo}
                    includeUndated={includeUndated}
                    setIncludeUndated={setIncludeUndated}
                    pickerSearchRef={pickerSearchRef}
                    pickerListRef={pickerListRef}
                    movePickerFocus={movePickerFocus}
                    pickerInvoices={pickerInvoices}
                    pickerLoad={pickerLoad}
                    pickerStock={pickerStock}
                    pickerProjects={pickerProjects}
                    pickerTotalNetCents={pickerTotalNetCents}
                    documentTallyByProject={documentTallyByProject}
                    stanceFor={stanceFor}
                    monthInPeriod={monthInPeriod}
                    relevantNetCents={relevantNetCents}
                    handleProjectClick={handleProjectClick}
                    openInvoice={openInvoice}
                    setFlatBooking={setFlatBooking}
                    onOpenProject={onOpenProject}
                    undatedTotal={undatedTotal}
                    autoDatableRows={autoDatableRows}
                    manualUndatedRows={manualUndatedRows}
                    obviousBusy={obviousBusy}
                    backlogBusy={backlogBusy}
                    backlogNote={backlogNote}
                    backlogError={backlogError}
                    backlogDateFor={backlogDateFor}
                    setBacklogDates={setBacklogDates}
                    handleDateObviousRows={handleDateObviousRows}
                    handleDateBacklog={handleDateBacklog}
                />
            )}

            {/* ── Die Rueckfrage-Kette nach dem Klick auf ein Projekt ──────
                Immer nur EIN Schritt sichtbar, und welcher, entscheidet
                invoicePickerFlow. Jeder traegt dieselbe Schrittanzeige, dasselbe
                Abbrechen und — ausser dem ersten — dasselbe Zurueck.

                Nur montiert, solange der jeweilige Schritt dran ist: damit ist
                jede Auswahl darin bei jedem Oeffnen zurueckgesetzt, ohne Effekt.
                Der Entwurf der Abschlagshoehe liegt bewusst ausserhalb (siehe
                advanceDraft) — er soll das Zurueckgehen ueberleben. */}
            {flow && currentPickerStep === 'billing-mode' && (
                <BillingModeStep
                    projectName={
                        flow.entry.project.name || flow.entry.project.project_number || 'Projekt'
                    }
                    docTypeLabel={
                        DOC_TYPE_CHOICES.find((c) => c.type === docType)?.label ?? 'Rechnung'
                    }
                    step={currentStepPosition('billing-mode')}
                    suggested={
                        invoiceConfig.defaultBillingMode === 'CUMULATIVE'
                            ? 'CUMULATIVE'
                            : 'PROGRESSIVE'
                    }
                    advancesUnknown={coverageLoad === 'failed'}
                    onCancel={() => setFlow(null)}
                    onChoose={(mode) => answerPickerStep({ mode })}
                />
            )}

            {flow && currentPickerStep === 'advance-amount' && (
                <AdvanceAmountStep
                    projectName={
                        flow.entry.project.name || flow.entry.project.project_number || 'Projekt'
                    }
                    step={currentStepPosition('advance-amount')}
                    // Dieselbe Bemessungsgrundlage, mit der createAdvanceInvoice
                    // rechnet: der Auftragswert, ersatzweise der offene Betrag.
                    basisNetCents={
                        flow.entry.contractedNetCents || flow.entry.netCents
                    }
                    choice={advanceDraft.choice}
                    onChange={(choice) =>
                        setAdvanceDraft({ projectId: flow.entry.project.id, choice })
                    }
                    onBack={hasPreviousStep() ? backPickerStep : undefined}
                    onCancel={() => setFlow(null)}
                    onSubmit={() => {
                        const read = readAdvanceAmount(advanceDraft.choice);
                        // Der Knopf ist ohne gueltige Eingabe gesperrt; die
                        // Pruefung hier ist das Netz, nicht die Meldung.
                        if (!read.result) return;
                        answerPickerStep({ advance: read.result });
                    }}
                />
            )}

            {flow && currentPickerStep === 'deduct-advances' && (
                <DeductAdvancesStep
                    projectName={
                        flow.entry.project.name || flow.entry.project.project_number || 'Projekt'
                    }
                    step={currentStepPosition('deduct-advances')}
                    count={deductibleAdvancesByProject.get(flow.entry.project.id) ?? 0}
                    onBack={hasPreviousStep() ? backPickerStep : undefined}
                    onCancel={() => setFlow(null)}
                    onChoose={(deduct) => answerPickerStep({ deductAdvances: deduct })}
                />
            )}

            {flow && currentPickerStep === 'prior-months' && (
                <PriorMonthsDialog
                    projectName={
                        flow.entry.project.name || flow.entry.project.project_number || 'Projekt'
                    }
                    // Aus der Zeile neu gerechnet und nicht mitgeschleppt: die
                    // Monate haengen am gewaehlten Zeitraum, und der ist
                    // waehrend der Kette unveraendert.
                    months={openMonthsBeforePeriod(flow.entry.months, periodFrom)}
                    step={currentStepPosition('prior-months')}
                    onBack={hasPreviousStep() ? backPickerStep : undefined}
                    onCancel={() => setFlow(null)}
                    // null heisst „gefragt und bewusst ohne" — etwas anderes als
                    // „noch nicht gefragt" (invoicePickerFlow).
                    onSkip={() => answerPickerStep({ overrideFrom: null })}
                    onInclude={(fromMonth) =>
                        // Der Erste des fruehesten einbezogenen Monats — der
                        // Beleg traegt damit eine zusammenhaengende Spanne.
                        answerPickerStep({ overrideFrom: `${fromMonth}-01` })
                    }
                />
            )}

            {/* MONATSPAUSCHALE: der Buchungsdialog des Monatsvorschlags.
                Geschrieben wird ueber flatRateBookingService (frisch laden,
                Schluessel-Riegel erneut); danach wird der Projektbestand neu
                geladen, damit Vorschlag und offene Betraege sofort stimmen. */}
            {flatBooking && (
                <FlatMonthBookingDialog
                    subtitle={flatBooking.projectName}
                    suggestions={flatBooking.suggestions}
                    onBook={async (s, quantity) => {
                        const outcome = await bookFlatMonthOnCloud({
                            projectId: flatBooking.projectId,
                            draft: {
                                flatServiceId: s.flatServiceId,
                                monthKey: s.monthKey,
                                quantity,
                            },
                            user: userProfile,
                        });
                        if (outcome.ok) {
                            // Dasselbe Muster wie nach dem Datieren der
                            // Protokolluecke: geaendert hat sich das
                            // Leistungsprotokoll, nicht die Rechnungslage.
                            await refreshProjects();
                        }
                        return outcome;
                    }}
                    onClose={() => setFlatBooking(null)}
                />
            )}
        </div>
    );
};

// Die eigene Kachel-Komponente ist entfallen: InvoiceKpiBar loest sie ab und
// haelt dabei Stichtags- und Flussgroessen auseinander, was die vier
// handgebauten Kacheln nicht taten.
